import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Card, Col, Row, Empty } from "antd";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Pie } from "react-chartjs-2";
import SkipAnalyticsContext from "../../../context/skip-analytics/context";
import AppContext from "../../../context/app/context";
import Spin from "../../general/antd/spin";
import styles from "./styles.module.scss";

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = [
  "rgba(255, 99, 132, 0.7)",
  "rgba(54, 162, 235, 0.7)",
  "rgba(255, 206, 86, 0.7)",
  "rgba(75, 192, 192, 0.7)",
  "rgba(153, 102, 255, 0.7)",
  "rgba(255, 159, 64, 0.7)",
  "rgba(199, 199, 199, 0.7)",
  "rgba(83, 102, 255, 0.7)",
];

const SkipReasonsChart = () => {
  const { t } = useTranslation();
  const { theme } = useContext(AppContext);
  const { reasonsBreakdown, loading } = useContext(SkipAnalyticsContext);

  if (loading.includes("reasonsBreakdown")) {
    return (
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card className={styles.card} title={t("store_skips")}>
            <div style={{ padding: "40px", textAlign: "center" }}>
              <Spin />
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card className={styles.card} title={t("order_skips")}>
            <div style={{ padding: "40px", textAlign: "center" }}>
              <Spin />
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  const storeSkipData = {
    labels: reasonsBreakdown?.storeSkipReasons?.map((r) => r.reasonName) || [],
    datasets: [
      {
        label: t("count"),
        data: reasonsBreakdown?.storeSkipReasons?.map((r) => r.count) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map((c) => c.replace("0.7", "1")),
        borderWidth: 1,
      },
    ],
  };

  const orderSkipData = {
    labels: reasonsBreakdown?.orderSkipReasons?.map((r) => r.reasonName) || [],
    datasets: [
      {
        label: t("count"),
        data: reasonsBreakdown?.orderSkipReasons?.map((r) => r.count) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map((c) => c.replace("0.7", "1")),
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: theme === "dark" ? "white" : "black",
          padding: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <Card
          className={styles.card}
          title={`${t("store_skips")} (${reasonsBreakdown?.totalStoreSkips || 0})`}
        >
          {reasonsBreakdown?.storeSkipReasons?.length ? (
            <div className={styles.chartContainer}>
              <Pie data={storeSkipData} options={options} />
            </div>
          ) : (
            <Empty description={t("no_data")} />
          )}
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card
          className={styles.card}
          title={`${t("order_skips")} (${reasonsBreakdown?.totalOrderSkips || 0})`}
        >
          {reasonsBreakdown?.orderSkipReasons?.length ? (
            <div className={styles.chartContainer}>
              <Pie data={orderSkipData} options={options} />
            </div>
          ) : (
            <Empty description={t("no_data")} />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default SkipReasonsChart;
